import { IDatabaseColumnModel } from "@kuika/kuika-cl-model";
import React from "react";
import { ManagedDbFilterOperator } from "../../services/table-editor-service";
import KuikaSelectbox from "../ui-kit/kuika-selectbox/kuika-selectbox";
import {
  defaultOperatorForColumn,
  IColumnFilterEntry,
  IOperatorOption,
  isValuelessOperator,
  operatorsForColumn,
  supportsNoneOperator
} from "./column-filters";

// KOA-5857 — Per-column filter cell.
//
// One operator picker + one value input, sharing a single border so the
// pair reads as one control. Pulled out of editable-data-grid.tsx so the
// FK picker (and any future surface that needs the same filter row UX)
// can render byte-identical filter cells without forking the logic.
//
// State contract:
//   - `entry === undefined` → no filter on this column (picker sits on
//     the column's default operator; value input empty).
//   - `entry !== undefined` → operator + value as provided.
// `onChange(null)` clears the entry. `onChange({operator, value})`
// upserts. The grid and picker use the same parent-managed filter map.

export interface IFilterOperatorOption {
  value: ManagedDbFilterOperator | "";
  label: string;
}

export interface IFilterCellProps {
  column: IDatabaseColumnModel;
  entry: IColumnFilterEntry | undefined;
  onChange: (next: IColumnFilterEntry | null) => void;
  // Optional ARIA prefix so the value input announces e.g. "FK browse:
  // Filter value for Email" when the cell lives inside a foreign-key
  // popover. Defaults to no prefix when omitted.
  ariaPrefix?: string;
}

const FilterCell: React.FC<IFilterCellProps> = (props: IFilterCellProps): JSX.Element => {
  const { column, entry, onChange, ariaPrefix } = props;

  const operators: IOperatorOption[] = operatorsForColumn(column);
  // Blob columns get a "None" sentinel as the picker's neutral state,
  // because every operator they expose is value-less — without "None",
  // landing on `isNull` or `isNotNull` immediately filters before the
  // user has chosen to. The sentinel is the empty string; KuikaSelectbox
  // handles it like any other option value and the change handler maps
  // it back to a null-clear on the parent's filter map.
  const wantsNone: boolean = supportsNoneOperator(column);
  const displayedOperator: ManagedDbFilterOperator | "" =
    entry !== undefined ? entry.operator : wantsNone ? "" : defaultOperatorForColumn(column);
  const valueless: boolean = displayedOperator !== "" && isValuelessOperator(displayedOperator);
  const value: string = entry?.value ?? "";

  const onOpChange = (next: string | number | (string | number)[] | null): void => {
    // KuikaSelectbox hands back the option's value directly. We only
    // accept the string codes our IOperatorOption set emits — anything
    // else (null clear, multi-select shape) is impossible here but
    // guarded against drift.
    if (typeof next !== "string") {
      return;
    }
    if (next === "") {
      // "None" sentinel — explicit no-filter choice on a blob column.
      // Clear the entry.
      onChange(null);
      return;
    }
    const nextOp: ManagedDbFilterOperator = next as ManagedDbFilterOperator;
    if (isValuelessOperator(nextOp)) {
      // Valueless operators bind no parameter; clear any stale value so
      // the displayed input doesn't carry meaningless text and the
      // export/query payload stays clean.
      onChange({ operator: nextOp, value: "" });
      return;
    }
    if (value.length === 0) {
      // Operator picked but no value yet — drop the entry so the filter
      // row stays semantically empty (no active filter pill counted).
      // The default operator is re-derived the next time the user types.
      onChange(null);
      return;
    }
    onChange({ operator: nextOp, value });
  };

  const onValueChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const nextValue: string = e.target.value;
    if (nextValue.length === 0) {
      onChange(null);
      return;
    }
    if (displayedOperator === "") {
      // The input is disabled in this state, but guard against a
      // programmatic event still slipping through.
      return;
    }
    onChange({ operator: displayedOperator, value: nextValue });
  };

  // English-verb labels only — Linear / Notion / Airtable convention.
  // The single-character math symbols (`∋`, `^`, `∅`) we used to render
  // alongside read as cryptic glyphs to anyone who isn't already steeped
  // in set notation; the verbs are universal. KuikaSelectbox renders
  // the label in both the closed trigger and the open dropdown, so
  // dropping the symbol leaves the picker reading "contains" / "starts
  // with" / "is empty" everywhere it appears.
  const operatorOptions: IFilterOperatorOption[] = operators.map((op: IOperatorOption): IFilterOperatorOption => ({
    value: op.value,
    label: op.label
  }));
  const selectboxOptions: IFilterOperatorOption[] = wantsNone
    ? [{ value: "", label: "None" }, ...operatorOptions]
    : operatorOptions;
  const activeOperatorLabel: string =
    displayedOperator === ""
      ? "None"
      : (operators.find((o: IOperatorOption): boolean => o.value === displayedOperator)?.label ?? "");

  const valueAriaLabel: string =
    ariaPrefix !== undefined ? `${ariaPrefix}: Filter value for ${column.name}` : `Filter value for ${column.name}`;
  const operatorAriaLabel: string =
    ariaPrefix !== undefined
      ? `${ariaPrefix}: Filter operator for ${column.name}: ${activeOperatorLabel}`
      : `Filter operator for ${column.name}: ${activeOperatorLabel}`;

  let placeholder: string;
  if (valueless || displayedOperator === "") {
    placeholder = "—";
  } else if (displayedOperator === "in" || displayedOperator === "notIn") {
    placeholder = "value1, value2, value3";
  } else {
    placeholder = "Filter…";
  }

  return (
    <div className="kuika-table-editor__filter-cell">
      <div className="kuika-table-editor__filter-op-cell" aria-label={operatorAriaLabel} title={activeOperatorLabel}>
        <KuikaSelectbox size="sm" value={displayedOperator} options={selectboxOptions} onChange={onOpChange} />
      </div>
      <input
        type="text"
        className="kuika-table-editor__filter-input"
        placeholder={placeholder}
        aria-label={valueAriaLabel}
        value={valueless || displayedOperator === "" ? "" : value}
        disabled={valueless || displayedOperator === ""}
        onChange={onValueChange}
      />
    </div>
  );
};

export default FilterCell;
